import {$qsAll} from '../../../@shared/ts/dom';

type ElementOptions = {
	loginId: string;
	clientKey: string;
	acceptSrc: string;
	tokenizationTimeoutMs?: number;
};

type Token = {
	token?: {
		descriptor: string;
		value: string;
	};
	error?: Array<{code: string; message: string}>;
	cancel?: boolean;
};

type AuthnetTokenizationResponse = {
	messages: {
		resultCode: 'Error' | 'Ok';
		message: Array<{
			code: string;
			text: string;
		}>;
	};
	opaqueData?: {
		dataDescriptor: string;
		dataValue: string;
	};
};

declare global {
	interface Window {
		peachpayAuthnetResponseHandler?: (response: AuthnetTokenizationResponse) => void;
	}
}

class AuthnetButtonElement {
	private static isTokenizing = false;

	private readonly loginId: string;
	private readonly clientKey: string;
	private readonly scriptSrc: string;
	private readonly tokenizationTimeoutMs: number;

	private $button: HTMLButtonElement | null;
	private $script: HTMLScriptElement | null;

	constructor(options: ElementOptions) {
		this.loginId = options.loginId;
		this.clientKey = options.clientKey;

		this.scriptSrc = options.acceptSrc;
		this.tokenizationTimeoutMs = options.tokenizationTimeoutMs ?? 30000;

		this.$script = null;
		this.$button = null;
	}

	public async tokenize(type: 'bank' | 'card'): Promise<Token> {
		if (AuthnetButtonElement.isTokenizing) {
			throw new Error('Tokenization is already in progress. Only 1 tokenization attempt may occur at a time.');
		}

		AuthnetButtonElement.isTokenizing = true;

		await this.mount(type);

		// This is an artificial delay to let the just mounted popup script load a little and prevent some rendering artifacts.
		await new Promise<void>(res => {
			setTimeout(() => {
				res();
			}, 1000);
		});

		return new Promise<Token>((resolve, reject) => {
			let settled = false;
			let observer: MutationObserver | null = null;
			let timeout: ReturnType<typeof setTimeout> | null = null;

			const cleanup = () => {
				if (timeout) {
					clearTimeout(timeout);
				}

				observer?.disconnect();
				window.peachpayAuthnetResponseHandler = undefined;
				AuthnetButtonElement.isTokenizing = false;
			};

			const settle = (token: Token) => {
				if (settled) {
					return;
				}

				settled = true;
				cleanup();
				resolve(token);
			};

			const fail = (error: Error) => {
				if (settled) {
					return;
				}

				settled = true;
				cleanup();
				reject(error);
			};

			if (!this.$button) {
				fail(new Error('Failed to locate Authorize.net tokenize button'));
				return;
			}

			window.peachpayAuthnetResponseHandler = (response: AuthnetTokenizationResponse) => {
				if (response.messages.resultCode === 'Ok' && response.opaqueData) {
					settle({
						token: {
							descriptor: response.opaqueData.dataDescriptor,
							value: response.opaqueData.dataValue,
						},
					});
				} else if (response.messages.resultCode === 'Error') {
					settle({
						error: response.messages.message.map(err => ({code: err.code, message: err.text})),
					});
				} else {
					fail(new Error('Unhandled Authorize.net tokenization response.'));
				}
			};

			if (type === 'bank') {
				timeout = setTimeout(() => {
					settle({
						error: [{
							code: 'E_TIMEOUT',
							message: 'Authorize.net tokenization timed out. Please try again.',
						}],
					});
				}, this.tokenizationTimeoutMs);
			}

			const Observer = window.MutationObserver;
			if (Observer) {
				observer = new Observer(mutations => {
					for (const mutation of mutations) {
						for (const removedNode of Array.from(mutation.removedNodes)) {
							if (removedNode instanceof HTMLElement && removedNode.id === 'AcceptUIContainer') {
								settle({cancel: true});
								return;
							}
						}
					}
				});
				observer.observe(document.body, {childList: true, subtree: true});
			}

			this.$button?.click();
		});
	}

	private async mount(type: 'bank' | 'card' | 'all'): Promise<void> {
		// Make sure everything is cleaned up before opening!
		this.unmount();

		return new Promise((resolve, reject) => {
			const paymentOptions = {
				showCreditCard: type === 'card' || type === 'all',
				showBankAccount: type === 'bank' || type === 'all',
			};

			const $button = document.createElement('button');
			$button.classList.add('AcceptUI');
			$button.setAttribute('style', 'display: none;');
			$button.setAttribute('type', 'button');
			$button.setAttribute('data-billingAddressOptions', '{"show":false,"required":false}');
			$button.setAttribute('data-apiLoginID', this.loginId);
			$button.setAttribute('data-clientKey', this.clientKey);
			$button.setAttribute('data-acceptUIFormBtnTxt', 'Submit');
			$button.setAttribute('data-acceptUIFormHeaderTxt', 'Card Information');
			$button.setAttribute('data-paymentOptions', JSON.stringify(paymentOptions));
			$button.setAttribute('data-responseHandler', 'peachpayAuthnetResponseHandler');
			$button.textContent = 'Pay';
			this.$button = $button;

			const $script = document.createElement('script');
			$script.type = 'text/javascript';
			$script.src = this.scriptSrc;
			$script.classList.add('pp-authnet-accept-script');
			$script.onload = () => {
				resolve();
			};

			$script.onerror = () => {
				reject(new Error('Failed to load tokenization script.'));
			};

			this.$script = $script;

			document.body.appendChild($button);
			document.body.appendChild($script);
		});
	}

	private unmount() {
		this.$script?.remove();
		this.$button?.remove();

		$qsAll('#AcceptUIBackground,#AcceptUIContainer', $el => {
			$el.remove();
		});
	}
}

export {
	AuthnetButtonElement,
	type Token,
};
